/**
 * 관세사 신고이력 깔때기 모델(52차) — 화면은 목록이 아니라 "분포 → 품명 → 원본" 순서로 좁힌다.
 *
 * 입력은 익명 이력 API(`broker_history_v1`)의 중복 집계 문서(품명·규격·성분·표준품명·세번·관측 수·날짜)와,
 * 있으면 집계 API(`broker_history_summary_v1`, 세번별 관측 합계)다. 집계 API가 없으면 표시된 상위 문서로
 * 분포를 만들고 그 사실을 화면이 밝힌다("표시된 N건 기준").
 *
 * 여기의 숫자는 관행(신고 분포)이지 판정이 아니다. 확정은 결정트리만 한다.
 */

const TOKEN = /[0-9a-z]+|[가-힣]+/g;
const HANGUL = /^[가-힣]+$/;

/** 서버(`star_history_server.tokens`)와 같은 규칙: 소문자, 영숫자·한글 연속 구간, 2자 이상, 중복 제거. */
export function queryTokens(q) {
  const seen = [];
  for (const t of String(q || "").toLowerCase().match(TOKEN) || []) {
    if (t.length >= 2 && !seen.includes(t)) seen.push(t);
  }
  return seen;
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** 검색어 낱말 중 글에 나타난 수 — 영숫자는 온전한 낱말(KIT은 KITCHEN이 아님), 한글은 부분 문자열. */
export function tokenHits(text, toks) {
  const low = String(text || "").toLowerCase();
  let n = 0;
  for (const t of toks) {
    if (HANGUL.test(t)) n += low.includes(t) ? 1 : 0;
    else n += new RegExp(`(?<![0-9a-z])${escapeRe(t)}(?![0-9a-z])`).test(low) ? 1 : 0;
  }
  return n;
}

/** 일치율(0~100) = 검색어 낱말 중 문서 글(품명·표준품명·규격·성분)에 있는 낱말의 비율. 낱말이 없으면 null. */
export function matchRate(nameText, toks) {
  if (!toks.length) return null;
  return Math.round((100 * tokenHits(nameText, toks)) / toks.length);
}

/** 품명 정규화 키 — 대문자, 구두점 제거, 공백 하나. 사무소가 달라 갈라진 같은 이름을 합친다. */
export function normalizeName(s) {
  return String(s || "").toUpperCase().replace(/[^0-9A-Z가-힣]+/g, " ").trim().replace(/\s+/g, " ");
}

export const digits = (v) => String(v ?? "").replace(/\D/g, "");
export const formatHsk = (hsk) => { const d = digits(hsk); return d.length === 10 ? `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}` : d; };

const PERIOD_MONTHS = { "12m": 12, "5y": 60, all: null };
export const PERIODS = [{ key: "12m", label: "최근 12개월" }, { key: "5y", label: "5년" }, { key: "all", label: "전체" }];

export function periodFloor(period, now = new Date()) {
  const months = PERIOD_MONTHS[period];
  if (!months) return null;
  const d = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** 방향·기간으로 문서를 거른다. 날짜가 없는 문서는 기간 제한이 있을 때 뺀다. */
export function filterItems(items, { direction = "all", from = null } = {}) {
  return (Array.isArray(items) ? items : []).filter((it) => {
    if (!it || digits(it.hsk).length < 4) return false;
    if (direction !== "all" && it.direction !== direction) return false;
    if (from && !(it.date && it.date >= from)) return false;
    return true;
  });
}

const obs = (it) => (Number.isSafeInteger(it?.observation_count) && it.observation_count > 0 ? it.observation_count : 1);

/**
 * 표시된 문서로 만든 세번별 분포. `partial: true`는 집계 API 없이 상위 문서만으로 만든 분포라는 뜻이다.
 * rows: [{ hsk, hs4, observations, documents, share(0~100), first, last }] 관측 수 내림차순.
 */
export function distributionFromItems(items) {
  const by = new Map();
  for (const it of items) {
    const hsk = digits(it.hsk);
    const row = by.get(hsk) || { hsk, hs4: hsk.slice(0, 4), observations: 0, documents: 0, first: null, last: null };
    row.observations += obs(it);
    row.documents += 1;
    if (it.first_date && (!row.first || it.first_date < row.first)) row.first = it.first_date;
    if (it.date && (!row.last || it.date > row.last)) row.last = it.date;
    by.set(hsk, row);
  }
  return finishDistribution([...by.values()], true);
}

/** 집계 API 응답(`by_hs[]`)으로 만든 분포. 형태가 어긋나면 null(호출자가 문서 분포로 대신한다). */
export function distributionFromSummary(summary) {
  if (!summary || summary.schema !== "broker_history_summary_v1" || !Array.isArray(summary.by_hs)) return null;
  const rows = summary.by_hs.filter((b) => b && digits(b.hsk).length >= 4 && Number.isSafeInteger(b.observations) && b.observations > 0)
    .map((b) => ({ hsk: digits(b.hsk), hs4: digits(b.hsk).slice(0, 4), observations: b.observations,
      documents: Number.isSafeInteger(b.documents) ? b.documents : 0, first: b.first_date || null, last: b.last_date || null,
      names: Array.isArray(b.names) ? b.names : [] }));
  const dist = finishDistribution(rows, false);
  // 58차(이력 트랙 회신 §1.1): 집계는 색인마다 점수 상위 N건 표본에서 낸다 — 관측·문서 수는 표본 안의 값이고 전체 분포가 아니다.
  dist.sampled = summary.sampled === true;
  dist.sampleDocuments = Number.isSafeInteger(summary.sample_documents) ? summary.sample_documents : null;
  dist.samplePerIndex = Number.isSafeInteger(summary.sample_per_index) ? summary.sample_per_index : null;
  dist.totalDocuments = Number.isSafeInteger(summary.total_documents) ? summary.total_documents : null;
  dist.totalLowerBound = summary.total_is_lower_bound === true;
  dist.otherDocuments = Number.isSafeInteger(summary.other_documents) ? summary.other_documents : 0;
  return dist;
}

function finishDistribution(rows, partial) {
  const total = rows.reduce((s, r) => s + r.observations, 0);
  rows.sort((a, b) => b.observations - a.observations || a.hsk.localeCompare(b.hsk));
  for (const r of rows) r.share = total ? Math.round((1000 * r.observations) / total) / 10 : 0;
  return { total, rows, partial };
}

/** 집중도 — 관행이 한 세번으로 모였는지, 둘로 갈렸는지, 흩어졌는지. */
export function concentration(dist) {
  const rows = dist?.rows || [];
  if (!rows.length) return { kind: "none", label: "분포 없음", topShare: 0 };
  const top = rows[0].share;
  const second = rows[1]?.share || 0;
  if (top >= 80) return { kind: "single", label: `한 세번 ${Math.round(top)}%`, topShare: top };
  if (top + second >= 80 && second >= 15) return { kind: "split", label: `둘로 갈림 ${Math.round(top)}·${Math.round(second)}%`, topShare: top };
  return { kind: "spread", label: `흩어짐 · 최다 ${Math.round(top)}%`, topShare: top };
}

/**
 * 선택한 세번의 신고품명 — 정규화 키로 합치고 일치율 → 관측 수 → 최근 순.
 * 각 행: { key, name, standard_name, spec, composition, observations, first, last, rate, specHit, direction, entries[] }
 */
export function mergeNames(items, toks) {
  const by = new Map();
  for (const it of items) {
    const key = normalizeName(it.name || it.standard_name) || `#${it.id}`;
    const row = by.get(key) || { key, name: it.name || it.standard_name || "—", standard_name: it.standard_name || "", spec: it.spec || "",
      composition: it.composition || "", observations: 0, first: null, last: null, rate: null, specHit: false, directions: new Set(), entries: [] };
    row.observations += obs(it);
    row.entries.push(it);
    if (it.first_date && (!row.first || it.first_date < row.first)) row.first = it.first_date;
    if (it.date && (!row.last || it.date > row.last)) row.last = it.date;
    if (it.direction) row.directions.add(it.direction);
    if ((it.spec_hits || 0) > 0 || tokenHits(`${it.spec || ""} ${it.composition || ""}`, toks) > 0) row.specHit = true;
    if (!row.spec && it.spec) row.spec = it.spec;
    if (!row.composition && it.composition) row.composition = it.composition;
    by.set(key, row);
  }
  // 일치율은 문서 전체 글(품명·표준품명·규격·성분)에 대고 잰다 — 서버 검색이 맞춘 칸과 같다("LAVER" + 규격 "SEASONED"는 100%).
  const rows = [...by.values()].map((r) => ({ ...r, rate: matchRate(`${r.name} ${r.standard_name} ${r.spec} ${r.composition}`, toks), directions: [...r.directions] }));
  rows.sort((a, b) => (b.rate ?? -1) - (a.rate ?? -1) || b.observations - a.observations || String(b.last || "").localeCompare(String(a.last || "")) || a.key.localeCompare(b.key));
  return rows;
}

/** 이력에서 배운 이름 — 표준품명·거래품명 가운데 관측이 많은 것 두 개(검색어와 같은 이름은 뺀다). 검색 칩으로 쓴다. */
export function learnedNames(items, query, limit = 2) {
  const qKey = normalizeName(query);
  const count = new Map();
  for (const it of items) {
    for (const cand of [it.standard_name, it.name]) {
      const key = normalizeName(cand);
      if (!key || key === qKey || key.length < 3 || /^\d+$/.test(key)) continue;
      count.set(key, (count.get(key) || 0) + obs(it));
    }
  }
  return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, limit).map(([name]) => name);
}

/** 결정트리 문맥(대화 열이 받는 것과 같은 `liveCodes`)에 비춘 세번 상태: 살아 있음 / 트리 밖 / 문맥 없음. */
export function treeStatus(hsk, ctx) {
  const codes = Array.isArray(ctx?.liveCodes) ? ctx.liveCodes.map(digits).filter(Boolean) : [];
  if (!codes.length) return null;
  const d = digits(hsk);
  return codes.some((c) => d.startsWith(c) || c.startsWith(d)) ? "live" : "outside";
}

/** 관세율표 상세(`heading-detail`) 행에서 그 세번의 이름·기본세율·FTA 최저를 뽑는다. 없으면 현행 표에 없는 세번이다. */
export function tariffInfo(hsk, detail) {
  const rows = Array.isArray(detail?.rows) ? detail.rows : [];
  const row = rows.find((r) => digits(r?.hsk) === digits(hsk));
  if (!row) return { found: false, name: null, basic: null, ftaMin: null };
  const rates = row.rates || {};
  const basic = rates.A != null ? rates.A : null;
  const fta = Object.entries(rates).filter(([code, v]) => /^F/.test(code) && typeof v === "number").map(([, v]) => v);
  return { found: true, name: row.name_ko || null, basic, ftaMin: fta.length ? Math.min(...fta) : null };
}
